import {
  CheckIcon,
  ClipboardDocumentIcon,
  QrCodeIcon
} from "@heroicons/react/24/outline";
import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import currencyService from "../../services/currencyService";
import orbitxService from "../../services/orbitxService";
import quickSwapService from "../../services/quickSwapService";
import CryptoBuyConfirmationModal from "./CryptoBuyConfirmationModal";
import TransactionHistory from "./TransactionHistory";
import SwapAmountInput from "./SwapAmountInput";

const BuyFromCrypto = () => {
  // Deposit-related state
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [selectedNetwork, setSelectedNetwork] = useState("");
  const [depositAddress, setDepositAddress] = useState("");
  const [userWallet, setUserWallet] = useState(null);
  const [feesAndLimits, setFeesAndLimits] = useState(null);
  const [qrCodeVisible, setQrCodeVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  // Swap-related state
  const [sellAmount, setSellAmount] = useState("");
  const [buyAmount, setBuyAmount] = useState("");
  const [btc1Price, setBtc1Price] = useState(null);
  const [availableCoins, setAvailableCoins] = useState({});
  const [swapCalculation, setSwapCalculation] = useState(null);

  // Flow control state
  const [currentStep, setCurrentStep] = useState("select"); // 'select', 'deposit', 'monitoring', 'swap', 'completed'
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [depositConfirmed, setDepositConfirmed] = useState(false);
  const [depositTxHash, setDepositTxHash] = useState("");
  const [showCryptoModal, setShowCryptoModal] = useState(false);
  const [cryptoSwapData, setCryptoSwapData] = useState(null);

  // Available currencies and networks
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [availableNetworks, setAvailableNetworks] = useState([]);

  // Monitoring interval ref
  const monitoringIntervalRef = useRef(null);
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    fetchInitialData();
    return () => {
      // Cleanup monitoring interval
      if (monitoringIntervalRef.current) {
        clearInterval(monitoringIntervalRef.current);
      }
    };
  }, []);

  // Recalculate swap amounts when inputs change
  useEffect(() => {
    // Don't calculate if values are incomplete (ending with "." or just "0.")
    if (sellAmount && (sellAmount.endsWith(".") || sellAmount === "0.")) {
      return;
    }
    if (buyAmount && (buyAmount.endsWith(".") || buyAmount === "0.")) {
      return;
    }

    if (
      Object.keys(availableCoins).length > 0 &&
      selectedCurrency &&
      btc1Price
    ) {
      // Clear any existing debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      // Debounce the calculation
      debounceTimerRef.current = setTimeout(() => {
        calculateSwapAmounts();
      }, 500);
    }

    // Cleanup function
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [availableCoins, selectedCurrency, btc1Price, sellAmount, buyAmount]);

  // Sync receive/pay amounts when price data becomes available
  useEffect(() => {
    if (
      !selectedCurrency ||
      !btc1Price ||
      Object.keys(availableCoins).length === 0
    ) {
      return;
    }

    if (sellAmount && parseFloat(sellAmount) > 0) {
      const calculatedBuy = calculateBuyAmount(sellAmount);
      if (calculatedBuy) {
        setBuyAmount(calculatedBuy);
      }
    } else if (buyAmount && parseFloat(buyAmount) > 0) {
      const calculatedSell = calculateSellAmount(buyAmount);
      if (calculatedSell) {
        setSellAmount(calculatedSell);
      }
    }
  }, [availableCoins, selectedCurrency, btc1Price]);

  // Start monitoring when deposit address is generated
  useEffect(() => {
    if (currentStep === "monitoring" && selectedCurrency && depositAddress) {
      startDepositMonitoring();
    }
  }, [currentStep, selectedCurrency, depositAddress]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);

      // Fetch BTC1 price and available coins
      await fetchBTC1Price();

      // Fetch available currencies for deposits
      await loadAvailableCurrencies();
    } catch (err) {
      console.error("Error fetching initial data:", err);
      setError("Failed to load initial data");
    } finally {
      setLoading(false);
    }
  };

  const fetchBTC1Price = async () => {
    try {
      let btc1PriceFromQuickSwap = null;

      // First, get the custom BTC1 price from the quick swap API
      const baseUrl =
        process.env.REACT_APP_API_BASE_URL || "http://localhost:8000/api";
      const btc1PriceResponse = await fetch(
        `${baseUrl}/v1/quick-swap/btc1-price/`
      );
      if (btc1PriceResponse.ok) {
        const btc1PriceData = await btc1PriceResponse.json();
        if (btc1PriceData.price) {
          btc1PriceFromQuickSwap = btc1PriceData.price;
          setBtc1Price(btc1PriceData.price);
          console.log(
            `BTC1 custom price from quick swap API: ${btc1PriceData.price}`
          );
        }
      } else {
        console.warn(
          "Failed to fetch custom BTC1 price, falling back to coins API"
        );
      }

      // Also get coins data for available currencies
      const coinsData = await orbitxService.getAvailableCoins();

      // Store all available coins for currency selection
      setAvailableCoins(coinsData.coins || {});

      // If we didn't get BTC1 price from quick swap API, fallback to coins data
      if (!btc1PriceFromQuickSwap) {
        const btc1UsdtPair = coinsData.pairs_data?.["BTC1-USDT"];
        if (btc1UsdtPair?.price) {
          setBtc1Price(btc1UsdtPair.price);
          console.log(
            `BTC1 price from pairs data (fallback): ${btc1UsdtPair.price}`
          );
        } else {
          // Final fallback
          const btc1CoinData = coinsData.coins?.BTC1;
          if (
            btc1CoinData?.price !== null &&
            btc1CoinData?.price !== undefined
          ) {
            setBtc1Price(btc1CoinData.price);
            console.log(
              `BTC1 price from coins data (fallback): ${btc1CoinData.price}`
            );
          } else {
            console.log(
              "No BTC1 price data available from API, using fallback"
            );
            setBtc1Price(108.34);
          }
        }
      }

      setError(""); // Clear any previous errors
    } catch (err) {
      console.error("Error fetching BTC1 price:", err);
      // Use fallback values on API error
      setBtc1Price(108.34);
      setError(""); // Clear error since we have a fallback
    }
  };

  const loadAvailableCurrencies = async () => {
    try {
      const currencies = await currencyService.getAvailableCurrencies();

      // Filter currencies that support deposits and are not BTC1
      const depositCurrencies = currencies.filter(
        (currency) => currency.code !== "BTC1" && !currency.disableTopups
      );

      setAvailableCurrencies(depositCurrencies);

      // Set first currency as default if no coin selected
      if (depositCurrencies.length > 0) {
        setSelectedCurrency(
          (current) => current || depositCurrencies[0].code,
        );
      }
    } catch (error) {
      console.error("Error loading available currencies:", error);
      toast.error("Failed to load currency data");
    }
  };

  // Load networks when currency changes
  const loadNetworksForCurrency = async (currencyCode) => {
    try {
      setLoading(true);
      const networks = await currencyService.getNetworksForCurrency(
        currencyCode
      );
      setAvailableNetworks(networks);

      // Set first network as default
      if (networks.length > 0) {
        setSelectedNetwork(networks[0].code);
      } else {
        setSelectedNetwork("");
      }

      // Reset wallet-related fields
      setDepositAddress("");
      setUserWallet(null);
      setFeesAndLimits(null);
    } catch (error) {
      console.error(`Error loading networks for ${currencyCode}:`, error);
      toast.error("Failed to load networks");
      setAvailableNetworks([]);
      setSelectedNetwork("");
    } finally {
      setLoading(false);
    }
  };

  // Load fees and limits when currency and network are selected
  const loadFeesAndLimits = async (currencyCode, networkCode) => {
    try {
      const feesData = await currencyService.getFeesAndLimits(
        currencyCode,
        networkCode
      );
      setFeesAndLimits(feesData);
      console.log("Fees and limits loaded:", feesData);
    } catch (error) {
      console.error(
        `Error loading fees and limits for ${currencyCode}/${networkCode}:`,
        error
      );
      setFeesAndLimits(null);
    }
  };

  // Calculate swap amounts using the API
  const calculateSwapAmounts = async () => {
    if (!selectedCurrency || !btc1Price) {
      console.log("Missing required data for calculation:", {
        selectedCurrency,
        btc1Price,
      });
      return;
    }

    // Check if user is authenticated
    const token = localStorage.getItem("access_token");
    if (!token) {
      console.log("User not authenticated, skipping calculation");
      return;
    }

    try {
      if (sellAmount && sellAmount !== "") {
        const sellNum = parseFloat(sellAmount);
        if (!isNaN(sellNum) && sellNum > 0) {
          console.log("Calculating swap amounts for sell:", {
            selectedCurrency,
            sellNum,
            btc1Price,
          });
          const calculation = await quickSwapService.calculateSwapAmounts(
            selectedCurrency,
            sellNum,
            btc1Price
          );
          setSwapCalculation(calculation);
          setBuyAmount(calculation.net_btc1_amount.toFixed(6));
        }
      } else if (buyAmount && buyAmount !== "") {
        const buyNum = parseFloat(buyAmount);
        if (!isNaN(buyNum) && buyNum > 0) {
          // Calculate reverse: how much source currency needed for this BTC1 amount
          const selectedCoinPrice =
            availableCoins[selectedCurrency]?.price ||
            getStaticPrice(selectedCurrency);
          const currentBtc1Price = btc1Price || 108;

          if (selectedCoinPrice && currentBtc1Price) {
            const buyValueInUSD = buyNum * currentBtc1Price;
            const sellAmount = buyValueInUSD / selectedCoinPrice;
            setSellAmount(sellAmount.toFixed(6));

            // Recalculate with the new sell amount
            console.log("Calculating swap amounts for buy:", {
              selectedCurrency,
              sellAmount,
              btc1Price,
            });
            const calculation = await quickSwapService.calculateSwapAmounts(
              selectedCurrency,
              parseFloat(sellAmount),
              btc1Price
            );
            setSwapCalculation(calculation);
          }
        }
      }
    } catch (error) {
      console.error("Error calculating swap amounts:", error);

      // Fallback to local calculation if API fails
      try {
        console.log("API failed, using fallback calculation");
        if (sellAmount && sellAmount !== "") {
          const sellNum = parseFloat(sellAmount);
          if (!isNaN(sellNum) && sellNum > 0) {
            const selectedCoinPrice =
              availableCoins[selectedCurrency]?.price ||
              getStaticPrice(selectedCurrency);
            const currentBtc1Price = btc1Price || 108;

            if (selectedCoinPrice && currentBtc1Price) {
              const sellValueInUSD = sellNum * selectedCoinPrice;
              const btc1Amount = sellValueInUSD / currentBtc1Price;
              const feeAmount = sellValueInUSD * 0.0; // 0% fee
              const netBtc1Amount = btc1Amount * 1.0; // After fee

              setSwapCalculation({
                source_amount: sellNum,
                source_currency: selectedCurrency,
                source_price_usd: selectedCoinPrice,
                usd_value: sellValueInUSD,
                btc1_amount: btc1Amount,
                btc1_price_usd: currentBtc1Price,
                exchange_rate: selectedCoinPrice / currentBtc1Price,
                fee_percentage: 0.0,
                fee_amount: feeAmount,
                net_btc1_amount: netBtc1Amount,
              });
              setBuyAmount(netBtc1Amount.toFixed(6));
            }
          }
        }
      } catch (fallbackError) {
        console.error("Fallback calculation also failed:", fallbackError);
        setError(
          "Failed to calculate swap amounts: " +
            (error.message || "Unknown error")
        );
      }
    }
  };

  // Check if user has wallet or create new one
  const checkOrCreateWallet = async () => {
    try {
      setLoading(true);

      // Validate that the selected network is valid for the selected currency
      const isNetworkValid = availableNetworks.some(
        (network) => network.code === selectedNetwork
      );
      if (!isNetworkValid) {
        console.warn(
          `Skipping wallet creation: Network ${selectedNetwork} not valid for ${selectedCurrency}`
        );
        return;
      }

      // Step 1: Check if wallet exists using getwallets API
      const getWalletPayload = { currency: selectedCurrency };

      // Only add blockchain_currency if it's different from the main currency (for tokens)
      if (selectedNetwork && selectedNetwork !== selectedCurrency) {
        getWalletPayload.blockchain_currency = selectedNetwork;
      }

      let walletResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/v1/getwallets/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          body: JSON.stringify(getWalletPayload),
        }
      );

      if (walletResponse.ok) {
        const walletData = await walletResponse.json();
        console.log("Existing wallets:", walletData);

        // Check if we found a matching wallet
        const existingWallet = walletData.find(
          (wallet) =>
            wallet.currency === selectedCurrency &&
            (selectedNetwork === selectedCurrency ||
              wallet.blockchain_currency === selectedNetwork)
        );

        if (existingWallet && !existingWallet.is_blocked) {
          // Use existing wallet
          setDepositAddress(existingWallet.address);
          setUserWallet(existingWallet);
          console.log("Using existing wallet:", existingWallet);
          return;
        }
      }

      // Step 2: Create new wallet if none exists or existing is blocked
      const createWalletPayload = { currency: selectedCurrency };

      // Only add blockchain_currency if it's different from the main currency (for tokens)
      if (selectedNetwork && selectedNetwork !== selectedCurrency) {
        createWalletPayload.blockchain_currency = selectedNetwork;
      }

      const createResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/v1/create_new_wallet/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          body: JSON.stringify(createWalletPayload),
        }
      );

      if (createResponse.ok) {
        const newWalletData = await createResponse.json();
        console.log("Created new wallet:", newWalletData);

        // Handle the response format (array of wallets)
        const newWallet = Array.isArray(newWalletData)
          ? newWalletData[0]
          : newWalletData;

        if (newWallet && newWallet.address) {
          setDepositAddress(newWallet.address);
          setUserWallet(newWallet);
          toast.success("New deposit address created!");

          if (newWallet.is_blocked) {
            toast.error("Deposits are currently blocked for this address");
          }
        } else {
          throw new Error("No wallet address returned from creation");
        }
      } else {
        let errorMessage = "Failed to create wallet";
        try {
          const errorData = await createResponse.json();
          errorMessage = errorData.error || errorMessage;

          // If it's a network mismatch error, log it but don't show to user
          // (This should be prevented by validation, but handle gracefully if it occurs)
          if (
            errorMessage.includes("not supported for") ||
            errorMessage.includes("Network")
          ) {
            console.warn(
              "Network mismatch error (should be prevented):",
              errorMessage
            );
            if (errorData.available_networks) {
              console.log("Available networks:", errorData.available_networks);
            }
            return; // Exit silently, don't show error to user
          }
        } catch (parseError) {
          console.error("Non-JSON error response:", parseError);
          if (createResponse.status === 500) {
            errorMessage = "Server error occurred. Please check server logs.";
          }
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("Error getting deposit address:", error);
      // Don't show network mismatch errors to user
      if (!error.message || !error.message.includes("not supported for")) {
        toast.error(error.message || "Failed to get deposit address");
      }
    } finally {
      setLoading(false);
    }
  };

  // Start deposit monitoring
  const startDepositMonitoring = async () => {
    if (!selectedCurrency || !depositAddress) {
      toast.error("No deposit address available for monitoring");
      return;
    }

    try {
      setLoading(true);

      // Call monitoring API
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/v1/monitor/deposits/?currency=${selectedCurrency}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log("Monitoring started:", data);
        //toast.success('Deposit monitoring is active');

        // Start polling for deposit confirmation
        startDepositPolling();
      } else {
        throw new Error("Failed to start monitoring");
      }
    } catch (error) {
      console.error("Error starting monitoring:", error);
      toast.error("Failed to start monitoring");
    } finally {
      setLoading(false);
    }
  };

  // Poll for deposit confirmation
  const startDepositPolling = () => {
    // Clear any existing interval
    if (monitoringIntervalRef.current) {
      clearInterval(monitoringIntervalRef.current);
    }

    // Poll every 10 seconds
    monitoringIntervalRef.current = setInterval(async () => {
      try {
        // Check wallet balance to see if deposit was received
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/v1/balances/`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          }
        );

        if (response.ok) {
          const balances = await response.json();
          const currencyBalance = balances[selectedCurrency];

          if (currencyBalance && parseFloat(currencyBalance.available) > 0) {
            // Deposit confirmed! Stop monitoring and proceed to swap
            clearInterval(monitoringIntervalRef.current);
            setDepositConfirmed(true);
            setCurrentStep("swap");
            toast.success(
              `Deposit confirmed! ${currencyBalance.available} ${selectedCurrency} received.`
            );

            // Auto-execute swap
            setTimeout(() => {
              executeSwap();
            }, 1000);
          }
        }
      } catch (error) {
        console.error("Error checking deposit status:", error);
      }
    }, 10000); // Check every 10 seconds
  };

  // Execute the swap
  const executeSwap = async () => {
    if (!selectedCurrency || !sellAmount || parseFloat(sellAmount) <= 0) {
      setError("Please enter a valid amount");
      return;
    }

    if (!btc1Price) {
      setError("Unable to get BTC1 price");
      return;
    }

    try {
      setIsExecuting(true);
      setError("");
      setCurrentStep("swap");

      const result = await quickSwapService.executeQuickSwap(
        selectedCurrency,
        parseFloat(sellAmount),
        btc1Price
      );

      console.log("Swap result:", result);

      if (result.success) {
        console.log("Swap successful, showing toast");

        // Prepare crypto swap data for modal
        const cryptoSwapModalData = {
          deposited_amount: parseFloat(sellAmount),
          from_currency: selectedCurrency,
          network_name: getCurrentNetwork()?.name || selectedCurrency,
          to_amount: parseFloat(result.to_amount),
          exchange_rate:
            swapCalculation?.exchange_rate ||
            parseFloat(sellAmount) / parseFloat(result.to_amount),
          fee_percentage: swapCalculation?.fee_percentage || 0,
          fee_amount: swapCalculation?.fee_amount || 0,
          transaction_id: result.transaction_id || result.tx_hash || "N/A",
          timestamp: new Date().toISOString(),
        };

        // Store swap data and show modal
        setCryptoSwapData(cryptoSwapModalData);
        setShowCryptoModal(true);

        // Show success toast notification
        toast.success(
          `Successfully swapped ${sellAmount} ${selectedCurrency} for ${result.to_amount} BTC1`,
          {
            icon: "🎉",
          }
        );

        setCurrentStep("completed");

        // Reset form after a delay
        setTimeout(() => {
          resetForm();
        }, 3000);
      } else {
        console.log("Swap failed:", result.error);
        const errorMessage = result.error || "Swap failed";
        setError(errorMessage);
        toast.error(errorMessage, {
          icon: "❌",
        });
        setCurrentStep("monitoring"); // Go back to monitoring
      }
    } catch (error) {
      console.error("Error executing swap:", error);
      const errorMessage = error.error || "Failed to execute swap";
      setError(errorMessage);
      toast.error(errorMessage, {
        icon: "❌",
      });
      setCurrentStep("monitoring"); // Go back to monitoring
    } finally {
      setIsExecuting(false);
    }
  };

  // Reset form to initial state
  const resetForm = () => {
    setCurrentStep("select");
    setSelectedCurrency("");
    setSelectedNetwork("");
    setDepositAddress("");
    setUserWallet(null);
    setSellAmount("");
    setBuyAmount("");
    setSwapCalculation(null);
    setDepositConfirmed(false);
    setDepositTxHash("");
    setError("");
    setShowCryptoModal(false);
    setCryptoSwapData(null);

    // Clear monitoring interval
    if (monitoringIntervalRef.current) {
      clearInterval(monitoringIntervalRef.current);
    }
  };

  // Static price fallbacks for when API data is null
  const getStaticPrice = (currency) => {
    const staticPrices = {
      BTC: 112923.01, // Updated from API data
      ETH: 4352.68, // Updated from API data
      USDT: 1,
      USDC: 1,
      DAI: 1,
      BNB: 600,
      MATIC: 0.8,
      FTT: 0.8189, // Updated from API data
      TRX: 0.1,
    };
    return staticPrices[currency] || 1;
  };

  // Format large numbers with commas
  const formatLargeNumber = (num) => {
    if (!num || num === '') return '';
    
    // Preserve the original string format if it's zero with decimals (e.g., "0.000")
    const number = parseFloat(num);
    if (isNaN(number)) {
      // If it's not a valid number but has content, return as-is (might be partial input like "0.")
      return num;
    }
    
    // If the number is zero and the original string contains a decimal point, preserve the format
    if (number === 0 && typeof num === 'string' && num.includes('.')) {
      // Extract the decimal part
      const parts = num.split('.');
      const decimalPart = parts[1] || '';
      // Preserve the original decimal places (up to 6)
      const preservedDecimals = decimalPart.substring(0, 6);
      // Return with preserved decimal places
      return `0.${preservedDecimals}`;
    }

    // For very large numbers, show without decimals
    if (number >= 1000000) {
      return number.toLocaleString('en-US', {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0
      });
    }

    // For smaller numbers, show with appropriate decimals
    return number.toLocaleString('en-US', {
      maximumFractionDigits: 6,
      minimumFractionDigits: 0
    });
  };

  const calculateBuyAmount = (sellValue) => {
    if (!sellValue || !selectedCurrency) return "";
    const sellNum = parseFloat(sellValue);
    if (isNaN(sellNum) || sellNum <= 0) return "";

    const selectedCoinPrice =
      availableCoins[selectedCurrency]?.price ||
      getStaticPrice(selectedCurrency);
    const currentBtc1Price = btc1Price || 108;

    if (!selectedCoinPrice || !currentBtc1Price) return "";

    const sellValueInUSD = sellNum * selectedCoinPrice;
    const btc1Amount = sellValueInUSD / currentBtc1Price;
    return btc1Amount.toFixed(6);
  };

  const calculateSellAmount = (buyValue) => {
    if (!buyValue || !selectedCurrency) return "";
    const buyNum = parseFloat(buyValue);
    if (isNaN(buyNum) || buyNum <= 0) return "";

    const selectedCoinPrice =
      availableCoins[selectedCurrency]?.price ||
      getStaticPrice(selectedCurrency);
    const currentBtc1Price = btc1Price || 108;

    if (!selectedCoinPrice || !currentBtc1Price) return "";

    const buyValueInUSD = buyNum * currentBtc1Price;
    const sellAmount = buyValueInUSD / selectedCoinPrice;
    return sellAmount.toFixed(6);
  };

  // Event handlers
  const handleCurrencyChange = (newCurrency) => {
    setSelectedCurrency(newCurrency);
    setSelectedNetwork("");
    setDepositAddress("");
    setUserWallet(null);
    setFeesAndLimits(null);
    setSellAmount("");
    setBuyAmount("");
    setSwapCalculation(null);
    setError("");

    // Load networks for the new currency
    if (newCurrency) {
      loadNetworksForCurrency(newCurrency);
    }
  };

  const handleNetworkChange = (newNetwork) => {
    setSelectedNetwork(newNetwork);
    setDepositAddress("");
    setUserWallet(null);
    setFeesAndLimits(null);
    setError("");

    // Load fees and limits for the new network
    if (selectedCurrency && newNetwork) {
      loadFeesAndLimits(selectedCurrency, newNetwork);
    }
  };

  const handleSellAmountChange = (value) => {
    setSellAmount(value);
    setError("");

    // Clear both fields and calculation if input is empty or exactly "0" (not "0." or "0.0")
    if (!value || value.trim() === "" || value === "0") {
      setBuyAmount("");
      setSwapCalculation(null);
      return;
    }

    // Also clear if the parsed value is 0 and it's not a decimal being typed (like "0.")
    const numValue = parseFloat(value);
    if (numValue === 0 && !value.includes(".")) {
      setBuyAmount("");
      setSwapCalculation(null);
      return;
    }

    // Don't calculate if the value is incomplete (ends with "." or is just "0.")
    if (value.endsWith(".") || value === "0.") {
      setBuyAmount("");
      setSwapCalculation(null);
      return;
    }

    setBuyAmount(calculateBuyAmount(value));
  };

  const handleBuyAmountChange = (value) => {
    setBuyAmount(value);
    setError("");

    // Clear both fields and calculation if input is empty or exactly "0" (not "0." or "0.0")
    if (!value || value.trim() === "" || value === "0") {
      setSellAmount("");
      setSwapCalculation(null);
      return;
    }

    // Also clear if the parsed value is 0 and it's not a decimal being typed (like "0.")
    const numValue = parseFloat(value);
    if (numValue === 0 && !value.includes(".")) {
      setSellAmount("");
      setSwapCalculation(null);
      return;
    }

    // Don't calculate if the value is incomplete (ends with "." or is just "0.")
    if (value.endsWith(".") || value === "0.") {
      setSellAmount("");
      setSwapCalculation(null);
      return;
    }

    setSellAmount(calculateSellAmount(value));
  };

  const handleGenerateDepositAddress = async () => {
    if (!selectedCurrency || !selectedNetwork) {
      setError("Please select both currency and network");
      return;
    }

    if (!sellAmount || parseFloat(sellAmount) <= 0) {
      setError("Please enter a valid amount to swap");
      return;
    }

    try {
      await checkOrCreateWallet();
      if (depositAddress) {
        setCurrentStep("deposit");
        toast.success("Deposit address generated!");
      }
    } catch (error) {
      console.error("Error generating deposit address:", error);
      setError("Failed to generate deposit address");
    }
  };

  const handleStartMonitoring = async () => {
    if (!depositAddress) {
      setError("No deposit address available");
      return;
    }

    try {
      setCurrentStep("monitoring");
      await startDepositMonitoring();
    } catch (error) {
      console.error("Error starting monitoring:", error);
      setError("Failed to start monitoring");
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Address copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy address");
    }
  };

  // Get current network info
  const getCurrentNetwork = () => {
    return availableNetworks.find(
      (network) => network.code === selectedNetwork
    );
  };

  // Get network confirmations
  const getNetworkConfirmations = () => {
    if (!selectedNetwork) return 6;
    return currencyService.getNetworkConfirmations(selectedNetwork);
  };

  // Get deposit limits
  const getDepositLimits = () => {
    if (!feesAndLimits) return { min: "0", max: "No limit" };
    return currencyService.getDepositLimits(feesAndLimits);
  };

  // Get network withdrawal fee
  const getNetworkWithdrawalFee = () => {
    if (!feesAndLimits || !selectedNetwork) return null;
    return currencyService.getNetworkWithdrawalFee(
      feesAndLimits,
      selectedNetwork,
      selectedCurrency
    );
  };

  // Format fee
  const formatFee = (fee) => {
    return currencyService.formatFee(fee);
  };

  if (loading && currentStep === "select") {
    return (
      <div className="p-8 text-center">
        <div className="loading-spinner mx-auto mb-4"></div>
        <p className="text-white">Loading currencies and prices...</p>
      </div>
    );
  }

  return (
    <div className="p-2 sm:p-4 lg:p-8 mt-12 sm:mt-0">
      <div className="max-w-md sm:max-w-lg lg:max-w-4xl mx-auto">
        <div className="bg-gray-900 rounded-lg p-3 sm:p-4 lg:p-6">
          <div className="space-y-3 sm:space-y-4">
            {/* Step Indicator */}
            <div className="flex items-center justify-center space-x-1 sm:space-x-2 md:space-x-4 mb-4 sm:mb-6 overflow-x-auto pb-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
              <div
                className={`flex items-center space-x-1 sm:space-x-2 flex-shrink-0 ${
                  currentStep === "select"
                    ? "text-white"
                    : currentStep === "deposit" ||
                      currentStep === "monitoring" ||
                      currentStep === "swap" ||
                      currentStep === "completed"
                    ? "text-white"
                    : "text-white"
                }`}
              >
                <div
                  className={`w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold ${
                    currentStep === "select"
                      ? "bg-[#0E1696] text-white"
                      : currentStep === "deposit" ||
                        currentStep === "monitoring" ||
                        currentStep === "swap" ||
                        currentStep === "completed"
                      ? "bg-green-400 text-white"
                      : "bg-gray-600 text-white"
                  }`}
                >
                  1
                </div>
                <span className="text-xs sm:text-sm hidden min-[375px]:inline">Select & Calculate</span>
                <span className="text-xs min-[375px]:hidden">Select</span>
              </div>
              <div className="w-4 sm:w-6 md:w-8 h-0.5 bg-gray-600 flex-shrink-0"></div>
              <div
                className={`flex items-center space-x-1 sm:space-x-2 flex-shrink-0 ${
                  currentStep === "deposit"
                    ? "text-white"
                    : currentStep === "monitoring" ||
                      currentStep === "swap" ||
                      currentStep === "completed"
                    ? "text-white"
                    : "text-white"
                }`}
              >
                <div
                  className={`w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold ${
                    currentStep === "deposit"
                      ? "bg-[#0E1696] text-white"
                      : currentStep === "monitoring" ||
                        currentStep === "swap" ||
                        currentStep === "completed"
                      ? "bg-green-400 text-white"
                      : "bg-gray-600 text-white"
                  }`}
                >
                  2
                </div>
                <span className="text-xs sm:text-sm">Deposit</span>
              </div>
              <div className="w-4 sm:w-6 md:w-8 h-0.5 bg-gray-600 flex-shrink-0"></div>
              <div
                className={`flex items-center space-x-1 sm:space-x-2 flex-shrink-0 ${
                  currentStep === "monitoring"
                    ? "text-white"
                    : currentStep === "swap" || currentStep === "completed"
                    ? "text-white"
                    : "text-white"
                }`}
              >
                <div
                  className={`w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold ${
                    currentStep === "monitoring"
                      ? "bg-[#0E1696] text-white"
                      : currentStep === "swap" || currentStep === "completed"
                      ? "bg-green-400 text-white"
                      : "bg-gray-600 text-white"
                  }`}
                >
                  3
                </div>
                <span className="text-xs sm:text-sm">Monitor</span>
              </div>
              <div className="w-4 sm:w-6 md:w-8 h-0.5 bg-gray-600 flex-shrink-0"></div>
              <div
                className={`flex items-center space-x-1 sm:space-x-2 flex-shrink-0 ${
                  currentStep === "swap"
                    ? "text-white"
                    : currentStep === "completed"
                    ? "text-white"
                    : "text-white"
                }`}
              >
                <div
                  className={`w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold ${
                    currentStep === "swap"
                      ? "bg-[#0E1696] text-white"
                      : currentStep === "completed"
                      ? "bg-green-400 text-white"
                      : "bg-gray-600 text-white"
                  }`}
                >
                  4
                </div>
                <span className="text-xs sm:text-sm">Swap</span>
              </div>
            </div>

            {/* Currency and Network Selection */}
            {(currentStep === "select" ||
              currentStep === "deposit" ||
              currentStep === "monitoring" ||
              currentStep === "swap") && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
                {/* Currency Selection */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-white mb-1.5 sm:mb-2">
                    Select Currency
                  </label>
                  <select
                    value={selectedCurrency}
                    onChange={(e) => handleCurrencyChange(e.target.value)}
                    className="w-full p-2.5 sm:p-3 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm sm:text-base focus:border-[#2A356D] focus:outline-none"
                    disabled={currentStep !== "select"}
                  >
                    <option value="">Select a currency</option>
                    {availableCurrencies.map((currency) => (
                      <option
                        key={currency.code}
                        value={currency.code}
                        className="bg-[#0E1696] text-white"
                      >
                        {currency.name} ({currency.code})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Network Selection */}
                {selectedCurrency && (
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-white mb-1.5 sm:mb-2">
                      Select Network
                    </label>
                    <select
                      value={selectedNetwork}
                      onChange={(e) => handleNetworkChange(e.target.value)}
                      className="w-full p-2.5 sm:p-3 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm sm:text-base focus:border-[#2A356D] focus:outline-none"
                      disabled={currentStep !== "select" || loading}
                    >
                      <option value="">
                        {loading ? "Loading networks..." : "Select a network"}
                      </option>
                      {availableNetworks.map((network) => (
                        <option
                          key={network.code}
                          value={network.code}
                          className="bg-[#0E1696] text-white"
                        >
                          {network.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            {/* Amount Input Section */}
            {(currentStep === "select" ||
              currentStep === "deposit" ||
              currentStep === "monitoring" ||
              currentStep === "swap") && (
              <>
                {/* Pay Section */}
                <div className="bg-gray-800 rounded-lg p-3 sm:p-4 lg:p-5 h-32 sm:h-36 lg:h-44 flex flex-col justify-between">
                  <label className="block text-xs sm:text-sm font-medium text-white mb-2 sm:mb-3">
                    You Pay
                  </label>
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-full max-w-[200px] sm:max-w-[300px] lg:max-w-[500px] overflow-hidden">
                      <SwapAmountInput
                        value={sellAmount}
                        onChange={handleSellAmountChange}
                        formatLargeNumber={formatLargeNumber}
                        disabled={currentStep !== "select"}
                      />
                    </div>
                    <div className="flex items-center space-x-1 sm:space-x-2 bg-gray-600 rounded-lg px-3 sm:px-4 lg:px-6 py-2 sm:py-3 lg:py-4 min-w-[80px] sm:min-w-[100px]">
                      <span className="text-sm sm:text-lg text-white">
                        {selectedCurrency || "---"}
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-between text-xs sm:text-sm text-white">
                    <span className="truncate">
                      $
                      {sellAmount
                        ? (
                            parseFloat(sellAmount) *
                            (availableCoins[selectedCurrency]?.price ||
                              getStaticPrice(selectedCurrency))
                          ).toFixed(2)
                        : "0"}
                    </span>
                    <span className="text-white truncate ml-2">
                      {sellAmount || "0"} {selectedCurrency || "---"}
                    </span>
                  </div>
                </div>

                {/* Swap Arrow */}
                <div className="flex justify-center">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#0E1696] !text-white rounded-full flex items-center justify-center">
                    <svg
                      className="w-4 h-4 sm:w-5 sm:h-5 !text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                      />
                    </svg>
                  </div>
                </div>

                {/* Receive Section */}
                <div className="bg-gray-800 rounded-lg p-3 sm:p-4 lg:p-5 h-32 sm:h-36 lg:h-44 flex flex-col justify-between">
                  <label className="block text-xs sm:text-sm font-medium text-white mb-2 sm:mb-3">
                    You Receive
                  </label>
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-full max-w-[200px] sm:max-w-[300px] lg:max-w-[500px] overflow-hidden">
                      <SwapAmountInput
                        value={buyAmount}
                        onChange={handleBuyAmountChange}
                        formatLargeNumber={formatLargeNumber}
                        disabled={currentStep !== "select"}
                      />
                    </div>
                    <div className="flex items-center space-x-1 sm:space-x-2 bg-gray-600 rounded-lg px-3 sm:px-4 lg:px-6 py-2 sm:py-3 lg:py-4 min-w-[80px] sm:min-w-[100px]">
                      <span className="text-sm sm:text-lg text-white">
                        ₿
                      </span>
                      <span className="text-white text-sm sm:text-lg font-medium ml-1 sm:ml-2">
                        BTC1
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-between text-xs sm:text-sm text-white">
                    <span className="truncate">
                      $
                      {buyAmount
                        ? (parseFloat(buyAmount) * btc1Price).toFixed(2)
                        : "0"}
                    </span>
                    <span className="text-white truncate ml-2">
                      ${btc1Price} per BTC1
                    </span>
                  </div>
                </div>

                {/* Swap Details */}
                {swapCalculation && (
                  <div className="bg-gray-800 rounded-lg p-3 sm:p-4 space-y-2 sm:space-y-2.5">
                    <div className="flex justify-between items-start sm:items-center gap-2 text-xs sm:text-sm text-white">
                      <span className="flex-shrink-0">Digital Asset Marketplace Rate:</span>
                      <span className="text-right break-words">
                        1 BTC1 = {1 / swapCalculation.exchange_rate.toFixed(6)}{" "}
                        {selectedCurrency}
                      </span>
                    </div>
                    <div className="flex justify-between items-start sm:items-center gap-2 text-xs sm:text-sm text-white">
                      <span className="flex-shrink-0">Fee ({swapCalculation.fee_percentage}%):</span>
                      <span className="text-right">${swapCalculation.fee_amount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-start sm:items-center gap-2 text-xs sm:text-sm text-white">
                      <span className="flex-shrink-0">You will receive:</span>
                      <span className="text-white font-medium text-right break-words">
                        {swapCalculation.net_btc1_amount.toFixed(6)} BTC1
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Deposit Address Section */}
            {currentStep === "deposit" && depositAddress && (
              <div className="bg-blue-900 bg-opacity-50 border border-blue-600 rounded-lg p-3 sm:p-4">
                <h4 className="text-blue-300 font-medium mb-2 sm:mb-3 text-sm sm:text-base">
                  Deposit Instructions
                </h4>
                <ol className="text-xs sm:text-sm text-blue-100 space-y-1 mb-3 sm:mb-4">
                  <li>1. Copy your deposit address below</li>
                  <li>
                    2. Send {selectedCurrency} from your external wallet to this
                    address
                  </li>
                  <li>3. Wait for {getNetworkConfirmations()} confirmations</li>
                  <li>4. Your balance will be updated automatically</li>
                </ol>

                <div className="bg-gray-700 rounded-lg p-3 sm:p-4">
                  <div className="flex items-center justify-between mb-2 sm:mb-3 flex-wrap gap-2">
                    <span className="text-xs sm:text-sm text-white">
                      {getCurrentNetwork()?.name} Network
                    </span>
                    <button
                      onClick={() => setQrCodeVisible(!qrCodeVisible)}
                      className="text-white hover:text-blue-300 text-xs sm:text-sm transition-colors whitespace-nowrap"
                    >
                      {qrCodeVisible ? "Hide QR" : "Show QR"}
                    </button>
                  </div>

                  <div className="break-all text-white text-xs sm:text-sm mb-2 sm:mb-3 font-mono bg-gray-800 p-2 sm:p-3 rounded overflow-x-auto">
                    {depositAddress}
                  </div>

                  <div className="space-y-2 sm:space-y-3">
                    <button
                      onClick={() => copyToClipboard(depositAddress)}
                      className="w-full flex items-center justify-center py-2 sm:py-2.5 px-4 bg-[#0E1696] hover:bg-blue-700 rounded-lg transition-colors text-sm sm:text-base font-medium"
                    >
                      {copied ? (
                        <CheckIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                      ) : (
                        <ClipboardDocumentIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                      )}
                      {copied ? "Copied!" : "Copy Address"}
                    </button>
                  </div>

                  {/* QR Code Placeholder */}
                  {qrCodeVisible && (
                    <div className="mt-3 sm:mt-4 flex justify-center">
                      <div className="w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 bg-white rounded-lg flex items-center justify-center">
                        <QrCodeIcon className="h-20 w-20 sm:h-24 sm:w-24 md:h-32 md:w-32 text-white" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Monitoring Status */}
            {currentStep === "monitoring" && (
              <div className="bg-yellow-900 bg-opacity-50 border border-[#2A356D] rounded-lg p-3 sm:p-4">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-b-2 border-[#2A356D] flex-shrink-0"></div>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-white font-medium text-sm sm:text-base">
                      Monitoring for Deposit
                    </h4>
                    <p className="text-yellow-100 text-xs sm:text-sm break-words">
                      Waiting for {selectedCurrency} deposit to be confirmed...
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Swap Execution Status */}
            {currentStep === "swap" && (
              <div className="bg-green-900 bg-opacity-50 border border-green-600 rounded-lg p-3 sm:p-4">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  {isExecuting ? (
                    <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-b-2 border-green-400 flex-shrink-0"></div>
                  ) : (
                    <CheckIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white flex-shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <h4 className="text-green-300 font-medium text-sm sm:text-base">
                      {isExecuting ? "Executing Swap..." : "Deposit Confirmed!"}
                    </h4>
                    <p className="text-green-100 text-xs sm:text-sm break-words">
                      {isExecuting
                        ? `Swapping ${sellAmount} ${selectedCurrency} for ${buyAmount} BTC1...`
                        : `Deposit confirmed! Executing swap...`}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Completion Status */}
            {currentStep === "completed" && (
              <div className="bg-green-900 bg-opacity-50 border border-green-600 rounded-lg p-3 sm:p-4">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <CheckIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <h4 className="text-green-300 font-medium text-sm sm:text-base">
                      Crypto Purchase Completed!
                    </h4>
                    <p className="text-green-100 text-xs sm:text-sm break-words">
                      Your deposit and swap were successful. Check the details
                      in the confirmation modal.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-red-900/20 border border-red-700 rounded-lg p-2 sm:p-3">
                <p className="text-red-400 text-xs sm:text-sm">{error}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              {currentStep === "select" && (
                <button
                  onClick={handleGenerateDepositAddress}
                  disabled={
                    !selectedCurrency ||
                    !selectedNetwork ||
                    !sellAmount ||
                    parseFloat(sellAmount) <= 0 ||
                    loading
                  }
                  className="w-full bg-[#0E1696] hover:bg-[#0E1696] text-white py-3 sm:py-4 text-sm sm:text-lg font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {loading ? "Loading..." : "Generate Deposit Address"}
                </button>
              )}

              {currentStep === "deposit" && (
                <button
                  onClick={handleStartMonitoring}
                  disabled={!depositAddress || loading}
                  className="w-full bg-blue-500 hover:bg-[#0E1696] text-white py-3 sm:py-4 text-sm sm:text-lg font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {loading ? "Starting..." : "Start Monitoring Deposits"}
                </button>
              )}

              {currentStep === "completed" && (
                <button
                  onClick={resetForm}
                  className="w-full bg-gray-500 hover:bg-gray-600 text-white py-3 sm:py-4 text-sm sm:text-lg font-semibold rounded-lg transition-all duration-200"
                >
                  Start New Swap
                </button>
              )}
            </div>

            {/* Network Details and Limits */}
            {getCurrentNetwork() &&
              feesAndLimits &&
              currentStep === "select" && (
                <div className="space-y-3 sm:space-y-4">
                  <div>
                    <h4 className="text-xs sm:text-sm font-medium text-white mb-2 sm:mb-3">
                      Network Info & Limits
                    </h4>
                    <div className="bg-gray-700 rounded-lg p-3 sm:p-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
                        <div>
                          <div className="text-white mb-1">Network</div>
                          <div className="text-white font-medium break-words">
                            {getCurrentNetwork().name}
                          </div>
                        </div>
                        <div>
                          <div className="text-white mb-1">Confirmations</div>
                          <div className="text-white font-medium">
                            {getNetworkConfirmations()}
                          </div>
                        </div>
                        <div>
                          <div className="text-white mb-1">Network Fee</div>
                          <div className="text-white font-medium break-words">
                            {getNetworkWithdrawalFee() !== null
                              ? `${formatFee(
                                  getNetworkWithdrawalFee()
                                )} ${selectedCurrency}`
                              : "N/A"}
                          </div>
                        </div>
                        <div>
                          <div className="text-white mb-1">Min Deposit</div>
                          <div className="text-white font-medium break-words">
                            {getDepositLimits().min} {selectedCurrency}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>

      {/* Transaction History Section */}
      <div className="mt-8">
        <TransactionHistory type="all" limit={5} showTitle={true} />
      </div>

      {/* Crypto Buy Confirmation Modal */}
      <CryptoBuyConfirmationModal
        isOpen={showCryptoModal}
        onClose={() => setShowCryptoModal(false)}
        swapData={cryptoSwapData}
      />
    </div>
  );
};

export default BuyFromCrypto;
